import {Fragment, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import { Menu, Bell, Settings, User, LogOut } from 'lucide-react';
import logo from '../../../../public/images/logo/g2g_blk.png';
import { useCurrentBalance } from '../files/queries/use-paginated-entries';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Base_url } from '../../config';

interface NavbarProps {
  shouldShowToggle: boolean;
  isMobileMode: boolean;
  setShowSideBar: React.Dispatch<React.SetStateAction<boolean>>;
  setShowMobileSideBar: React.Dispatch<React.SetStateAction<boolean>>;
}

export function Navbar({
  shouldShowToggle,
  isMobileMode,
  setShowSideBar,
  setShowMobileSideBar,
}: NavbarProps) {
  const [authType, setAuthType] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('authType');
    }
    return null;
  });

  const [userEmail, setUserEmail] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('userEmail');
    }
    return null;
  });

  const [userName, setUserName] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('userName');
    }
    return null;
  });

  const [showNotifications, setShowNotifications] = useState(false);
  const queryClient = useQueryClient();

  // Fetch current balance
  const { data: balanceData } = useCurrentBalance();
  const displayBalance = balanceData?.balance || '0';

  // Fetch notifications
  const { data: notificationsData, refetch: refetchNotifications } = useQuery({
    queryKey: ['notifications', userEmail],
    queryFn: async () => {
      try {
        const response = await axios.get(`${Base_url}v1/notifications`, {
          params: { user_email: userEmail }
        });
        return response?.data || { notifications: [], unread_count: 0 };
      } catch (error) {
        console.error('Error fetching notifications:', error);
        return { notifications: [], unread_count: 0 };
      }
    },
    enabled: authType === 'gmail' && !!userEmail, // Only fetch when user is logged in and has email
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const unreadCount = notificationsData?.unread_count || 0;
  const notifications = notificationsData?.notifications || [];

  // Mark notification as read
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await axios.post(`${Base_url}v1/notifications/${notificationId}/mark-read`, {}, {
        params: { user_email: userEmail }
      });
    },
    onSuccess: () => {
      refetchNotifications();
    },
  });

  // Mark all as read
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      await axios.post(`${Base_url}v1/notifications/mark-all-read`, {}, {
        params: { user_email: userEmail }
      });
    },
    onSuccess: () => {
      refetchNotifications();
    },
  });

  const handleNotificationClick = (notification: any) => {
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.id);
    }
    // Close the dropdown after clicking
    setShowNotifications(false);
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  useEffect(() => {
    const type = localStorage.getItem('authType');
    const email = localStorage.getItem('userEmail');
    const name = localStorage.getItem('userName');
    setAuthType(type);
    setUserEmail(email);
    setUserName(name);
  }, []);

  return (
    <Fragment>
      <div className="navbar-gradient-animate dashboard-grid-navbar navbar-container">
        {/* Logo Section */}
        <Link
          to="/"
          onClick={() => {
            if (!isMobileMode) {
              setShowSideBar(true);
              setShowMobileSideBar(false);
            }
          }}
          className="navbar-logo-link"
          aria-label={'Go to homepage'}
        >
          <div className="navbar-logo-box">
            <img src={logo} alt="G2G Logo" className="navbar-logo-image" />
          </div>
          {!isMobileMode && (
            <div>
              <h1 className="navbar-title">
                G2G VAULT
              </h1>
              <p className="navbar-subtitle">
                AI Powered Digital Signature Platform
              </p>
            </div>
          )}
        </Link>

        {/* Right Side Actions */}
        <div className="navbar-actions" style={{ gap: isMobileMode ? '12px' : '24px' }}>

          {/* Mobile Menu Toggle */}
          {shouldShowToggle && (
            <button
              onClick={() => {
                setShowMobileSideBar(true);
                setShowSideBar(true);
              }}
              className="navbar-btn"
            >
              <Menu className="navbar-icon" />
            </button>
          )}

          {/* Balance Display */}
          {authType === 'gmail' && (
            <div className="navbar-balance">
              {!isMobileMode && (
                <span className="navbar-balance-label">Balance:</span>
              )}
              <span className="navbar-balance-amount">
                ₹{Number(displayBalance).toFixed(2)}
              </span>
            </div>
          )}

          {/* Notification Bell Icon */}
          {authType === 'gmail' && (
            <div className="navbar-notification">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="navbar-btn relative"
              >
                <Bell className="navbar-icon" />
                {unreadCount > 0 && (
                  <span className="navbar-notification-badge">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <div className="navbar-notification-dropdown">
                  <div className="navbar-notification-header">
                    <h3 className="navbar-notification-title">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllAsRead}
                        className="navbar-notification-mark-all"
                        disabled={markAllAsReadMutation.isPending}
                      >
                        Mark all read
                      </button>
                    )}
                    <button
                      onClick={() => setShowNotifications(false)}
                      className="navbar-notification-close"
                    >
                      ×
                    </button>
                  </div>
                  <div className="navbar-notification-body">
                    {notifications.length > 0 ? (
                      notifications.map((notification: any) => (
                        <div
                          key={notification.id}
                          className={`navbar-notification-item ${
                            !notification.is_read ? 'navbar-notification-item-unread' : ''
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="navbar-notification-item-header">
                            <h4 className="navbar-notification-item-title">
                              {notification.title}
                            </h4>
                            <span className="navbar-notification-item-time">
                              {new Date(notification.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="navbar-notification-item-message">
                            {notification.message}
                          </p>
                          {!notification.is_read && (
                            <div className="navbar-notification-item-dot"></div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="navbar-notification-empty">
                        No notifications
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Wallet Button OR Gmail User Display */}
          {authType !== 'gmail' ? (
            <div className="wallet-button">
              {/* @ts-ignore */}
              <w3m-button />
            </div>
          ) : (
            <div className="navbar-gmail">
              {/* User name display */}
              {!isMobileMode && (
                <div className="navbar-email-box">
                  <User className="navbar-email-icon" />
                  <span className="navbar-email-text">
                    {userName || userEmail}
                  </span>
                </div>
              )}

              {/* Logout button */}
              <button
                onClick={() => {
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('authType');
                  localStorage.removeItem('userEmail');
                  window.location.href = '/';
                }}
                className="navbar-logout"
              >
                <LogOut className="navbar-logout-icon" />
                {!isMobileMode && <span>Logout</span>}
              </button>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
}

export function getColorStyle(color: string, textColor?: string): string {
  switch (color) {
    case 'primary':
      return `bg-primary ${textColor || 'text-on-primary'} border-b-primary`;
    default:
      return `${color} ${textColor}`;
  }
}
